import React, { useState, useEffect } from 'react';
import ProtectedRoute from '../components/ProtectedRoute';
import DashboardLayout from '../components/DashboardLayout';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

import {
  Search,
  Trash2,
  Edit,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { useCallback } from 'react';

const AdminUsers = () => {
  const { hasRole } = useAuth();
  const isAdmin = hasRole('ADMIN');
  const pageTitle = isAdmin ? 'User Management' : 'User Overview';

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [roleNames, setRoleNames] = useState([]);

  // UI State
  const [searchTerm, setSearchTerm] = useState('');

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      // Fallback to /admin/users if your backend handles role-based access control on the same endpoint
      const response = await api.get('/admin/users'); // Assuming API checks token roles
      // Augment data with visual mocks if needed
      const augmentedUsers = (response.data || []).map(u => ({
        ...u,
        joinedDate: u.createdAt ? new Date(u.createdAt).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        }) : 'N/A',
        avatarColor: ['bg-blue-500', 'bg-green-500', 'bg-yellow-500', 'bg-purple-500'][Math.floor(Math.random() * 4)]
      }));
      setUsers(augmentedUsers);
    } catch (err) {
      setError(err.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []); // api is from import, outside of component instance

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);


  const handleToggleStatus = async (userId, currentStatus) => {
    if (!isAdmin) return; // Guard clause
    try {
      await api.put(`/admin/users/${userId}/status`, {
        isActive: !currentStatus,
      });
      fetchUsers();
    } catch (err) {
      setError(err.message || 'Failed to update user status');
    }
  };

  const handleEditRoles = (user) => {
    if (!isAdmin) return; // Guard clause
    setSelectedUser(user);
    setRoleNames(user.roles?.map(r => r.name) || []);
    setShowRoleModal(true);
  };

  const handleUpdateRoles = async () => {
    try {
      await api.put(`/admin/users/${selectedUser.id}/roles`, {
        roleNames: roleNames,
      });
      setShowRoleModal(false);
      fetchUsers();
    } catch (err) {
      setError(err.message || 'Failed to update user roles');
    }
  };

  const availableRoles = ['USER', 'MODERATOR', 'ADMIN'];

  // Filtering
  const filteredUsers = users.filter(user => {
    const matchesSearch = user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  if (loading) {
    return (
      <ProtectedRoute roles={['ADMIN', 'MODERATOR']}>
        <DashboardLayout title={pageTitle} roles={['ADMIN', 'MODERATOR']}>
          <div className="flex items-center justify-center min-h-96">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        </DashboardLayout>
      </ProtectedRoute>
    );
  }

  return (
    <ProtectedRoute roles={['ADMIN', 'MODERATOR']}>
      <DashboardLayout roles={['ADMIN', 'MODERATOR']} title={pageTitle}>
        <div className="space-y-6">
          {/* Header */}
          <div className="text-center md:text-left space-y-2">
            {/* <h1 className="text-3xl font-bold text-gray-900"></h1> */}
            <p className="text-gray-500">Manage all users in one place. Control access, assign roles, and monitor activity across your platform.</p>
          </div>

          {/* Toolbar */}
          <div className="flex flex-col md:flex-row gap-4 justify-between items-center bg-white p-4 rounded-xl shadow-sm border border-gray-100">
            <div className="w-full">
              {/* Search */}
              <div className="relative w-full">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <input
                  type="text"
                  placeholder="Search"
                  className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
          </div>

          {error && (
            <div className="p-4 bg-red-50 text-red-800 rounded-lg border border-red-100">
              {error}
            </div>
          )}

          {/* Desktop Table */}
          <div className="hidden md:block bg-white shadow-sm rounded-xl border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-900 text-white">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-medium uppercase tracking-wider">Full Name</th>
                    <th className="px-6 py-4 text-left text-xs font-medium uppercase tracking-wider">Email</th>
                    <th className="px-6 py-4 text-left text-xs font-medium uppercase tracking-wider">Status</th>
                    <th className="px-6 py-4 text-left text-xs font-medium uppercase tracking-wider">Role</th>
                    <th className="px-6 py-4 text-left text-xs font-medium uppercase tracking-wider">Joined Date</th>
                    <th className="px-6 py-4 text-right text-xs font-medium uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {filteredUsers.map((user, index) => (
                    <tr key={user.id || index} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className={`h-8 w-8 rounded-full ${user.avatarColor || 'bg-gray-300'} flex items-center justify-center text-white font-medium text-xs mr-3`}>
                            {user.name?.charAt(0) || 'U'}
                          </div>
                          <div className="text-sm font-medium text-gray-900">{user.name || 'N/A'}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{user.email}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${user.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                          }`}>
                          {user.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {user.roles?.map(r => r.name).join(', ') || 'USER'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{user.joinedDate}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end gap-2">
                          <button onClick={() => handleEditRoles(user)} className="text-gray-400 hover:text-blue-600">
                            <Edit size={18} />
                          </button>
                          <button onClick={() => handleToggleStatus(user.id, user.isActive)} className={`${user.isActive ? 'text-gray-400 hover:text-red-600' : 'text-gray-400 hover:text-green-600'}`}>
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile Card View */}
          <div className="md:hidden space-y-4">
            {filteredUsers.map((user, index) => (
              <div key={user.id || index} className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`h-10 w-10 rounded-full ${user.avatarColor || 'bg-gray-300'} flex items-center justify-center text-white font-bold`}>
                      {user.name?.charAt(0) || 'U'}
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{user.name || 'N/A'}</h3>
                      <p className="text-sm text-gray-500">{user.email}</p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 text-sm mb-4">
                  <div>
                    <p className="text-gray-500 text-xs uppercase font-medium mb-1">Status</p>
                    <span className={`px-2 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full ${user.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                      }`}>
                      {user.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div>
                    <p className="text-gray-500 text-xs uppercase font-medium mb-1">Role</p>
                    <p className="font-medium text-gray-700">{user.roles?.map(r => r.name).join(', ') || 'USER'}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 text-xs uppercase font-medium mb-1">Joined</p>
                    <p className="text-gray-700">{user.joinedDate}</p>
                  </div>
                </div>

                <div className="flex gap-2 pt-3 border-t border-gray-100">
                  <button
                    onClick={() => handleEditRoles(user)}
                    className="flex-1 flex items-center justify-center gap-2 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100"
                  >
                    <Edit size={16} /> Edit Role
                  </button>
                  <button
                    onClick={() => handleToggleStatus(user.id, user.isActive)}
                    className={`flex-1 flex items-center justify-center gap-2 py-2 text-sm font-medium rounded-lg ${user.isActive
                      ? 'text-red-600 bg-red-50 hover:bg-red-100'
                      : 'text-green-600 bg-green-50 hover:bg-green-100'
                      }`}
                  >
                    {user.isActive ? 'Deactivate' : 'Activate'}
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 pt-4 border-t border-gray-200">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <span className="whitespace-nowrap">Rows per page</span>
              <select className="border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500">
                <option>10</option>
                <option>25</option>
                <option>50</option>
              </select>
              <span className="whitespace-nowrap">of {filteredUsers.length} rows</span>
            </div>

            <div className="flex gap-2">
              <button className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-600 disabled:opacity-50">
                <ChevronLeft size={18} />
              </button>
              <div className="flex items-center justify-center w-8 h-8 bg-gray-900 text-white rounded-lg text-sm font-medium">1</div>
              <button className="flex items-center justify-center w-8 h-8 border border-gray-200 hover:bg-gray-50 rounded-lg text-sm font-medium text-gray-600">2</button>
              <button className="flex items-center justify-center w-8 h-8 border border-gray-200 hover:bg-gray-50 rounded-lg text-sm font-medium text-gray-600">3</button>
              <span className="flex items-center justify-center w-8 h-8 text-gray-400">...</span>
              <button className="flex items-center justify-center w-8 h-8 border border-gray-200 hover:bg-gray-50 rounded-lg text-sm font-medium text-gray-600">10</button>
              <button className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-600 disabled:opacity-50">
                <ChevronRight size={18} />
              </button>
            </div>
          </div>

          {/* Role Edit Modal */}
          {showRoleModal && (
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
              <div className="relative bg-white p-6 md:p-8 rounded-2xl shadow-xl max-w-md w-full">
                <div className="space-y-4">
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">
                      Edit Roles
                    </h3>
                    <p className="text-gray-500 text-sm mt-1">Select roles for <span className="font-medium text-gray-900">{selectedUser?.email}</span></p>
                  </div>

                  <div className="space-y-3 py-4">
                    {availableRoles.map((role) => (
                      <label key={role} className="flex items-center p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                        <input
                          type="checkbox"
                          checked={roleNames.includes(role)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setRoleNames([...roleNames, role]);
                            } else {
                              setRoleNames(roleNames.filter(r => r !== role));
                            }
                          }}
                          className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                        />
                        <span className="ml-3 font-medium text-gray-700 capitalize">{role.toLowerCase()}</span>
                      </label>
                    ))}
                  </div>

                  <div className="flex gap-3 pt-2">
                    <button
                      onClick={() => setShowRoleModal(false)}
                      className="flex-1 px-4 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleUpdateRoles}
                      className="flex-1 px-4 py-2.5 bg-gray-900 text-white rounded-lg hover:bg-gray-800 font-medium"
                    >
                      Update Permissions
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </DashboardLayout>
    </ProtectedRoute>
  );
};

export default AdminUsers;
